import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@comps/ui/command";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { getCurrentMonth, getCurrentYear, getPeriodMonthLabel, Period, PeriodMonth, PeriodMonthValues, PeriodTypeMonth, PeriodYear, PeriodYearValues } from "@dto/period";
import { TW_ChoiceScrollArea, TW_CommonCommandInput } from "@utils/twClasses";
import { Square, SquareCheck } from "lucide-react";
import { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PeriodEditionFormProps } from "./PeriodCell";

export const PeriodMonthAndYearEditionForm = memo(function PeriodMonthAndYearEditionForm({ field, value, updatePeriod, searchValue, setSearchValue, searchInputRef, displaySearch = true, hasValidation = true, disabled = false }: PeriodEditionFormProps) {

    const { t } = useTranslation();
    const [monthValue, setMonthValue] = useState(value?.month() ?? getCurrentMonth());
    const [yearValue, setYearValue] = useState(value?.year() ?? getCurrentYear());
    const [modified, setModified] = useState(value === undefined);

    // useEffect(() => {
    //     if (!hasValidation && !disabled && monthValue !== value?.month() && yearValue !== value?.year()) {
    //         validate(monthValue, yearValue);
    //     }
    // }, []);

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (!setSearchValue || disabled) {
            return;
        }
        switch (event.key) {
            case "Escape":
                event.preventDefault();
                setSearchValue("");
                break;
            case "Enter":
                if (searchValue === "") {
                    event.preventDefault();
                    event.stopPropagation();
                    validate(monthValue, yearValue);
                } else {
                    event.stopPropagation();
                    setSearchValue("");
                }
                break;
            case "Tab":
                event.preventDefault();
                validate(monthValue, yearValue);
                setSearchValue("");
                break;
        }
    }

    const onMonthChange = (monthValue: string) => {
        changeMonth(parseInt(monthValue) as PeriodMonth);
    }

    const onMonthLabelChange = (monthLabel: string) => {
        const month = Period.monthValue(monthLabel, t);
        changeMonth(month);
    }

    function changeMonth(month: PeriodMonth) {
        if (month === monthValue) {
            return;
        }
        setMonthValue(month);
        if (setSearchValue) {
            setSearchValue("");
        }
        setModified(true);
        if (!hasValidation) {
            validate(month, yearValue);
        }
    }

    const onYearChange = (yearName: string) => {
        if (yearName === yearValue.toString()) {
            return;
        }
        const year = parseInt(yearName);
        setYearValue(year);
        if (setSearchValue) {
            setSearchValue("");
        }
        setModified(true);
        if (!hasValidation) {
            validate(monthValue, year);
        }
    }

    function validate(month: number | undefined, year: number | undefined) {
        if (disabled) {
            return;
        }
        if (month === value?.month() && year === value?.year()) {
            return;
        }
        if (!month || !year) {
            updatePeriod(undefined);
        } else {
            const newPeriod = Period.newPeriod(year, month as PeriodMonth, 1, PeriodTypeMonth);
            updatePeriod(newPeriod);
        }
        setModified(false);
    }

    const onValidateClick = () => {
        validate(monthValue, yearValue);
    }

    const hasSearch = displaySearch && setSearchValue;

    return (
        <div key={"filter-month-" + field?._key} className={"flex flex-col sm:flex-row flex-start items-stretch sm:items-start gap-2 " + (hasSearch ? " w-full " : " w-48 ")}>
            {hasSearch &&
                <div className="flex flex-col flex-start items-center gap-1 grow">
                    <Command onKeyDown={onKeyDown} className="min-w-0">
                        <CommandInput
                            placeholder={t("data.search.placeholder")}
                            className={TW_CommonCommandInput + " h-10 min-h-10"}
                            autoFocus={true}
                            value={searchValue}
                            onValueChange={(value) => setSearchValue(value)}
                            ref={searchInputRef}
                            disabled={disabled}
                        />
                        {searchValue === "" &&
                            <Select onValueChange={onMonthChange} value={monthValue.toString()} disabled={disabled}>
                                <SelectTrigger className="w-full grow mt-1 outline-none focus:outline-none">
                                    <SelectValue placeholder={t("field.dialog.link.name.placeholder")} />
                                </SelectTrigger>
                                <SelectContent>
                                    {PeriodMonthValues.map((month: PeriodMonth) => {
                                        const monthLabel = getPeriodMonthLabel(month, t);
                                        const currMonthValue = month.toString();
                                        return (
                                            <SelectItem key={currMonthValue} value={currMonthValue} onSelect={() => onMonthChange(currMonthValue)}>{monthLabel}</SelectItem>
                                        )
                                    })}
                                </SelectContent>
                            </Select>
                        }
                        {searchValue !== "" &&
                            <div className={TW_ChoiceScrollArea}>
                                <CommandList className="h-full max-h-full w-full">
                                    <CommandEmpty>{t("data.table.noChoiceItems")}</CommandEmpty>
                                    <CommandGroup>
                                        {PeriodMonthValues.map((month: PeriodMonth) => {
                                            const monthLabel = getPeriodMonthLabel(month, t);
                                            return (
                                                <CommandItem key={month} value={monthLabel} onSelect={() => onMonthLabelChange(monthLabel)}>{monthLabel}</CommandItem>
                                            )
                                        })}
                                    </CommandGroup>
                                </CommandList>
                            </div>
                        }
                    </Command>
                </div>
            }
            {!hasSearch &&
                <Select onValueChange={onMonthChange} value={monthValue.toString()} disabled={disabled}>
                    <SelectTrigger className={"h-9 grow outline-none focus:outline-none" + (hasSearch ? " w-24 min-w-24 " : " w-full")}>
                        <SelectValue placeholder={t("field.dialog.link.name.placeholder")} />
                    </SelectTrigger>
                    <SelectContent>
                        {PeriodMonthValues.map((month: PeriodMonth) => {
                            const monthLabel = getPeriodMonthLabel(month, t);
                            const currMonthValue = month.toString();
                            return (
                                <SelectItem key={currMonthValue} value={currMonthValue} onSelect={() => onMonthChange(currMonthValue)}>{monthLabel}</SelectItem>
                            )
                        })}
                    </SelectContent>
                </Select>
            }
            <div className="flex flex-col flex-start items-center gap-0 grow">
                {hasSearch &&
                    <div className="hidden sm:block w-full h-10 min-h-10 border-none border-transparent"></div>
                }
                <div className={"flex flex-row flex-start items-center gap-1" + (hasSearch ? " mt-1" : "")}>
                    <Select onValueChange={onYearChange} value={yearValue.toString()} disabled={disabled}>
                        <SelectTrigger>
                            <SelectValue placeholder={t("field.dialog.link.name.placeholder")} />
                        </SelectTrigger>
                        <SelectContent>
                            {PeriodYearValues.map((year: PeriodYear) => {
                                const currYearValue = year.toString();
                                return (
                                    <SelectItem key={currYearValue} value={currYearValue} onSelect={() => onYearChange(currYearValue)}>{currYearValue}</SelectItem>
                                )
                            })}
                        </SelectContent>
                    </Select>
                    {hasValidation && modified && <Square onClick={onValidateClick} className="w-6 h-6 min-w-6 min-h-6 text-gray-500 cursor-pointer" />}
                    {hasValidation && !modified && <SquareCheck className="w-6 h-6 min-w-6 min-h-6 text-green-500 cursor-default" />}
                </div>
            </div>
        </div>
    )
    }, (prevProps, nextProps) => {
    return prevProps.field === nextProps.field && prevProps.value === nextProps.value && prevProps.searchValue === nextProps.searchValue && prevProps.searchInputRef === nextProps.searchInputRef && prevProps.disabled === nextProps.disabled;
});